import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type { Category } from '../App';
import * as api from '../utils/api';
import { supabase } from '../utils/supabase/client';

const getPINCodesFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('pinCodes');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getOperatorsFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('operators');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

interface LoginPageProps {
  onLogin: (pinCode?: string, roleName?: string, allowedProjects?: string[], roleType?: 'operator' | 'vendor' | 'supervisor' | 'microsoft' | 'employee', vendorId?: string, personName?: string, accountId?: string) => void;
  onAdminLogin: () => void;
  categories: Category[];
}

type LoginMode = 'options' | 'pin' | 'microsoft' | 'operator' | 'admin';

export function LoginPage({ onLogin, onAdminLogin }: LoginPageProps) {
  const [loginMode, setLoginMode] = useState<LoginMode>('options');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [personName, setPersonName] = useState('');
  const [operatorEmail, setOperatorEmail] = useState('');
  
  // Microsoft login state — initialize to true immediately if this is an OAuth callback
  // so there's no flash of the login screen before the session is processed
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    return hash.includes('access_token') || hash.includes('error_description') || search.includes('code=');
  });
  const handledMicrosoftEmailRef = useRef<string | null>(null);
  
  // Admin login fields
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const isLocalEnvironment = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const clearOAuthSessionHints = () => {
    sessionStorage.removeItem('oauthLoginType');
    sessionStorage.removeItem('oauthAuthenticatedRole');
  };

  const getFriendlyMicrosoftErrorMessage = (error?: string) => {
    if (!error) {
      return 'Microsoft sign-in failed. Please try again.';
    }

    const normalizedError = error.trim().toLowerCase();

    if (normalizedError.includes('not authorized') || normalizedError.includes('inactive')) {
      return 'This email is not approved for supervisor access.';
    }

    if (normalizedError.includes('email is required')) {
      return 'No email was returned from Microsoft sign-in.';
    }

    if (normalizedError.includes('failed to validate') || normalizedError.includes('unable to validate')) {
      return 'Could not verify your Microsoft account.';
    }

    if (
      normalizedError.includes('failed to fetch') ||
      normalizedError.includes('networkerror') ||
      normalizedError.includes('network request failed') ||
      normalizedError.includes('load failed')
    ) {
      return 'Could not reach the sign-in service.';
    }

    return 'Microsoft sign-in failed. Please try again.';
  };

  const handlePINSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that person name is provided
    if (!personName.trim()) {
      alert('Please enter your full name.');
      return;
    }

    // Get PIN codes from storage
    const pinCodes = getPINCodesFromStorage();
    
    // Find matching PIN
    const matchedPIN = pinCodes.find((p: any) => p.code === pin.trim());
    
    if (matchedPIN) {
      // Infer roleType if not present (for backward compatibility with old PIN codes)
      let roleType = matchedPIN.roleType;
      if (!roleType) {
        // If no roleType is set, infer from vendorId
        roleType = matchedPIN.vendorId === 'gama-operator' ? 'operator' : 'vendor';
      }
      
      // Successful PIN login - pass projects instead of categories
      onLogin(matchedPIN.code, matchedPIN.vendorName, matchedPIN.projects || [], roleType, matchedPIN.vendorId, personName.trim());
    } else {
      alert('Invalid PIN code. Please try again.');
    }
  };

  const finishSupervisorMicrosoftLogin = async (email: string, displayName?: string | null) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) return;
      if (handledMicrosoftEmailRef.current === normalizedEmail) return;
      handledMicrosoftEmailRef.current = normalizedEmail;

      const result = await api.authorizeMicrosoftAccount(normalizedEmail);
      
      if (result.valid && result.account) {
        sessionStorage.setItem('oauthAuthenticatedRole', 'supervisor');
        onLogin(
          undefined,
          'Gama Supervisor',
          result.account.allFutureProjects ? ['*'] : (result.account.projects || []),
          'microsoft',
          result.account.id, // supervisor account ID used for task-level access filtering
          result.account.name || displayName || normalizedEmail,
          result.account.id
        );
      } else {
        await supabase.auth.signOut();
        handledMicrosoftEmailRef.current = null;
        clearOAuthSessionHints();
        alert(getFriendlyMicrosoftErrorMessage(result.error));
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      await supabase.auth.signOut();
      handledMicrosoftEmailRef.current = null;
      clearOAuthSessionHints();
      alert(getFriendlyMicrosoftErrorMessage(error instanceof Error ? error.message : undefined));
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const finishEmployeeMicrosoftLogin = async (email: string, displayName?: string | null) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) return;
      if (handledMicrosoftEmailRef.current === normalizedEmail) return;
      handledMicrosoftEmailRef.current = normalizedEmail;

      const result = await api.validateEmployeeAccount(normalizedEmail);

      if (result.valid && result.employee) {
        sessionStorage.setItem('oauthAuthenticatedRole', 'employee');
        onLogin(
          undefined,
          result.employee.email,
          result.employee.projectIds || [],
          'employee',
          undefined,
          result.employee.name || displayName || normalizedEmail,
          result.employee.id
        );
      } else {
        await supabase.auth.signOut();
        handledMicrosoftEmailRef.current = null;
        clearOAuthSessionHints();
        alert(result.error === 'This email is not approved for employee access'
          ? 'This email is not approved for employee access.'
          : getFriendlyMicrosoftErrorMessage(result.error));
      }
    } catch (error) {
      console.error('Employee Microsoft login error:', error);
      await supabase.auth.signOut();
      handledMicrosoftEmailRef.current = null;
      clearOAuthSessionHints();
      alert(getFriendlyMicrosoftErrorMessage(error instanceof Error ? error.message : undefined));
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const handleMicrosoftSignIn = async (loginType: 'supervisor' | 'employee') => {
    try {
      setIsMicrosoftLoading(true);
      sessionStorage.setItem('oauthLoginType', loginType);
      const redirectTo = `${window.location.origin}${window.location.pathname}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo,
          scopes: 'email openid profile',
        },
      });

      if (error) {
        console.error('Microsoft OAuth start error:', error);
        setIsMicrosoftLoading(false);
        alert('Could not start Microsoft sign-in.');
      }
    } catch (error) {
      console.error('Microsoft OAuth start error:', error);
      setIsMicrosoftLoading(false);
      alert('Could not start Microsoft sign-in.');
    }
  };

  const handleOperatorLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedEmail = operatorEmail.trim().toLowerCase();
    const trimmedPin = pin.trim();

    if (!normalizedEmail) {
      alert('Please enter your email address.');
      return;
    }
    if (!trimmedPin) {
      alert('Please enter your PIN code.');
      return;
    }

    const operators = getOperatorsFromStorage();
    const matched = operators.find(
      (op: any) => op.email === normalizedEmail && op.pin === trimmedPin && op.isActive !== false
    );

    if (!matched) {
      alert('Invalid email or PIN. Please try again.');
      return;
    }

    const allowedProjects = matched.allFutureProjects !== false ? ['*'] : (matched.projectIds || ['*']);
    onLogin(
      undefined,
      matched.email,
      allowedProjects,
      'operator',
      undefined,
      matched.email,
      matched.id
    );
  };

  useEffect(() => {
    let active = true;

    const isOAuthInProgress = () => {
      const hasUrlParams = window.location.hash.includes('access_token') ||
        window.location.hash.includes('error_description') ||
        window.location.search.includes('code=');
      const hasHint = !!sessionStorage.getItem('oauthLoginType') ||
        !!sessionStorage.getItem('oauthAuthenticatedRole');
      return hasUrlParams || hasHint;
    };

    const processSession = async () => {
      if (!isOAuthInProgress()) return;

      const { data } = await supabase.auth.getSession();
      const session = data.session;
      const email = session?.user?.email;
      if (!active) return;
      if (session?.user && !email) {
        setIsMicrosoftLoading(false);
        alert('No email was returned from Microsoft sign-in.');
        return;
      }
      if (!email) return;

      setIsMicrosoftLoading(true);
      const loginType = sessionStorage.getItem('oauthAuthenticatedRole') || sessionStorage.getItem('oauthLoginType');
      if (loginType === 'employee') {
        await finishEmployeeMicrosoftLogin(email, session.user.user_metadata?.full_name || session.user.user_metadata?.name || null);
      } else {
        await finishSupervisorMicrosoftLogin(email, session.user.user_metadata?.full_name || session.user.user_metadata?.name || null);
      }
    };

    processSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'SIGNED_IN' && session?.user?.email && isOAuthInProgress()) {
        setIsMicrosoftLoading(true);
        const loginType = sessionStorage.getItem('oauthAuthenticatedRole') || sessionStorage.getItem('oauthLoginType');
        if (loginType === 'employee') {
          void finishEmployeeMicrosoftLogin(
            session.user.email,
            session.user.user_metadata?.full_name || session.user.user_metadata?.name || null
          );
        } else {
          void finishSupervisorMicrosoftLogin(
            session.user.email,
            session.user.user_metadata?.full_name || session.user.user_metadata?.name || null
          );
        }
      }
      if (event === 'SIGNED_OUT') {
        handledMicrosoftEmailRef.current = null;
        clearOAuthSessionHints();
      }
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleAdminSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Hardcoded admin credentials
    const ADMIN_USERNAME = 'GamaLLC';
    const ADMIN_PASSWORD = 'GamaLLC_softwarE';

    if (adminUsername === ADMIN_USERNAME && adminPassword === ADMIN_PASSWORD) {
      onAdminLogin();
    } else {
      alert('Invalid admin credentials. Please try again.');
    }
  };

  const resetForm = () => {
    setLoginMode('options');
    setPin('');
    setPersonName('');
    setOperatorEmail('');
    setAdminUsername('');
    setAdminPassword('');
    setShowPin(false);
    setShowAdminPassword(false);
    setIsMicrosoftLoading(false);
  };

  if (isMicrosoftLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full flex flex-col items-center gap-6">
          <svg
            className="w-10 h-10 text-indigo-600 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">Logging in</p>
            <p className="text-sm text-gray-500 mt-1">Verifying your Microsoft account...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      {/* Admin Login Button - Top Right */}
      <button
        onClick={() => setLoginMode('admin')}
        className="absolute top-6 right-6 bg-gray-900 hover:bg-gray-800 text-white font-medium py-2 px-6 rounded-lg transition duration-200 shadow-lg"
      >
        Admin Login
      </button>

      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
          <p className="text-gray-600">
            {loginMode === 'admin' ? 'Admin Portal Access' : 'Login to get started'}
          </p>
        </div>

        {/* LOGIN OPTIONS */}
        {loginMode === 'options' && (
          <div className="space-y-4">
            {/* Microsoft Sign In Button */}
            <button
              onClick={() => setLoginMode('microsoft')}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-sm hover:shadow-md"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 23 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 0h10.931v10.931H0V0z" fill="#F25022" />
                <path d="M12.069 0H23v10.931H12.069V0z" fill="#7FBA00" />
                <path d="M0 12.069h10.931V23H0V12.069z" fill="#00A4EF" />
                <path d="M12.069 12.069H23V23H12.069V12.069z" fill="#FFB900" />
              </svg>
              Supervisor Microsoft Login
            </button>

            <button
              onClick={() => setLoginMode('operator')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              Operator Login
            </button>

            <button
              onClick={() => setLoginMode('pin')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              Vendor Login
            </button>

            {isLocalEnvironment && (
              <button
                onClick={() => onLogin(undefined, 'Test Supervisor', ['*'], 'microsoft', 'dev-supervisor-001', 'Test Supervisor', 'dev-supervisor-001')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
              >
                [Dev] Supervisor Login
              </button>
            )}
          </div>
        )}

        {/* ADMIN LOGIN FORM */}
        {loginMode === 'admin' && (
          <form onSubmit={handleAdminSubmit} className="space-y-4">
            <div>
              <label htmlFor="adminUsername" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="adminUsername"
                value={adminUsername}
                onChange={(e) => setAdminUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                placeholder="Enter admin username"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showAdminPassword ? "text" : "password"}
                  id="adminPassword"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                  placeholder="Enter admin password"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                >
                  {showAdminPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              Admin Login
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition"
            >
              Back to login options
            </button>
          </form>
        )}

        {/* MICROSOFT LOGIN FORM */}
        {loginMode === 'microsoft' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              Login with your Microsoft work account. Access will only be granted if your email has been approved by the admin.
            </div>
            <button
              type="button"
              onClick={() => handleMicrosoftSignIn('supervisor')}
              disabled={isMicrosoftLoading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              {isMicrosoftLoading ? 'Connecting to Microsoft...' : 'Continue with Microsoft'}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition"
            >
              Back to login options
            </button>
          </div>
        )}

        {loginMode === 'operator' && (
          <form onSubmit={handleOperatorLogin} className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Login with your work email and PIN code.
            </div>

            <div>
              <label htmlFor="operatorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="operatorEmail"
                value={operatorEmail}
                onChange={(e) => setOperatorEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                placeholder="Enter your work email"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="operatorPin" className="block text-sm font-medium text-gray-700 mb-2">
                PIN Code
              </label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  id="operatorPin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                  placeholder="Enter your PIN"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                >
                  {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              Sign In
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition"
            >
              Back to login options
            </button>
          </form>
        )}

        {/* PIN LOGIN FORM */}
        {loginMode === 'pin' && (
          <form onSubmit={handlePINSubmit} className="space-y-4">
            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                Enter PIN Code
              </label>
              <div className="relative">
                <input
                  type={showPin ? "text" : "password"}
                  id="pin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                  placeholder="Enter your PIN"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                >
                  {showPin ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="personName" className="block text-sm font-medium text-gray-700 mb-2">
                Enter your Full Name
              </label>
              <input
                type="text"
                id="personName"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                placeholder="Enter your full name"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 shadow-lg hover:shadow-xl"
            >
              Sign In
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition"
            >
              Back to login options
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
